/* eslint-disable */
import React, { useEffect, useState } from 'react'
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  CircularProgress,
} from '@mui/material'
import CheckCircle from '@mui/icons-material/CheckCircle'
import WarningAmber from '@mui/icons-material/WarningAmber'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import FolderOff from '@mui/icons-material/FolderOff'
import { loadFatSupportForRec } from '../../utils/fatSupportDocs'
import { OIKOS_FILES_BASE } from '../../constants/oikosReceitas'
import ModalOikosDocsViewer from '../Modals/ModalOikosDocsViewer/ModalOikosDocsViewer'

const fmt = (d) => {
  if (!d) return ''
  const s = String(d).slice(0, 10)
  const [y, m, day] = s.split('-')
  if (!y || !m || !day) return s
  return `${day}/${m}/${y}`
}

const STATUS_UI = {
  ok: {
    color: '#2e7d32',
    title: 'Documentação de suporte ok',
    Icon: CheckCircle,
  },
  expired: {
    color: '#ed6c02',
    title: 'Há documento de suporte vencido',
    Icon: WarningAmber,
  },
  missing: {
    color: '#d32f2f',
    title: 'Falta documento de suporte',
    Icon: ErrorOutline,
  },
  empty: {
    color: '#9aa3ad',
    title: 'Nenhum documento de suporte selecionado',
    Icon: FolderOff,
  },
}

const statusChip = (row) => {
  if (row.status === 'ok') {
    return <Chip size="small" color="success" label={row.documento_fimVig ? `Válido até ${fmt(row.documento_fimVig)}` : 'Vigente'} />
  }
  if (row.status === 'expired') {
    return <Chip size="small" color="warning" label={row.documento_fimVig ? `Vencido em ${fmt(row.documento_fimVig)}` : 'Sem validade'} />
  }
  return <Chip size="small" color="error" variant="outlined" label="Sem arquivo" />
}

export default function FatSupportDocsIcon({ rec, data }) {
  const [loading, setLoading] = useState(!data)
  const [payload, setPayload] = useState(data || null)
  const [open, setOpen] = useState(false)
  const [pdfFiles, setPdfFiles] = useState(null)
  const [pdfIndex, setPdfIndex] = useState(0)

  useEffect(() => {
    if (data) {
      setPayload(data)
      setLoading(false)
      return undefined
    }
    if (!rec) return undefined
    let cancel = false
    setLoading(true)
    loadFatSupportForRec(rec)
      .then((p) => {
        if (!cancel) setPayload(p)
      })
      .catch(() => {
        if (!cancel) setPayload({ rows: [], summary: 'empty', projId: 0 })
      })
      .finally(() => {
        if (!cancel) setLoading(false)
      })
    return () => { cancel = true }
  }, [rec?.orcRubr_id, data])

  const summary = payload?.summary || 'empty'
  const ui = STATUS_UI[summary] || STATUS_UI.empty
  const Icon = ui.Icon
  const rows = payload?.rows || []
  const faltando = rows.filter((r) => r.status !== 'ok')
  const viewable = rows.filter((r) => r.arquivo_caminho)

  const openPdf = (row) => {
    const path = String(row.arquivo_caminho || '')
    const isPdf = path.toLowerCase().split('?')[0].endsWith('.pdf')
    if (!isPdf) {
      window.open(`${OIKOS_FILES_BASE.replace(/\/?$/, '/')}${path.replace(/^\//, '')}`, '_blank')
      return
    }
    const files = viewable
      .filter((r) => String(r.arquivo_caminho || '').toLowerCase().split('?')[0].endsWith('.pdf'))
      .map((r) => ({
        id: r.documento_id,
        label: r.label,
        arquivo_caminho: r.arquivo_caminho,
      }))
    const idx = Math.max(0, files.findIndex((f) => Number(f.id) === Number(row.documento_id)))
    setPdfIndex(idx)
    setPdfFiles(files)
  }

  return (
    <>
      <IconButton
        size="small"
        title={ui.title}
        onClick={(e) => {
          e.stopPropagation()
          setOpen(true)
        }}
        disabled={loading}
        sx={{ color: loading ? '#c5c9ce' : ui.color }}
      >
        {loading ? <CircularProgress size={16} /> : <Icon fontSize="small" />}
      </IconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>Documentação de suporte</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Typography variant="body2">Carregando…</Typography>
          ) : rows.length === 0 ? (
            <Typography variant="body2">
              Nenhum documento de suporte foi marcado no processo de faturamento da aba Eventos de pagamento. O e-mail da nota seguiria só com XML e PDF.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {faltando.length > 0 && (
                <Typography variant="body2" color="error">
                  Faltando ou vencido: {faltando.map((r) => r.label).join('; ')}.
                </Typography>
              )}
              {rows.map((row) => (
                <Box
                  key={row.key}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.25,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" sx={{ flex: 1 }}>{row.label}</Typography>
                    {statusChip(row)}
                    {row.arquivo_caminho ? (
                      <Button size="small" onClick={() => openPdf(row)}>Ver PDF</Button>
                    ) : null}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <ModalOikosDocsViewer
        open={Array.isArray(pdfFiles)}
        onClose={() => setPdfFiles(null)}
        title="Documento de suporte"
        files={pdfFiles || []}
        initialIndex={pdfIndex}
        fileBaseUrl={OIKOS_FILES_BASE}
      />
    </>
  )
}
